import React, { useRef } from 'react';
import style from './desktopHeader.module.scss';
import Logo from 'icons/logo';
import SectionLink from 'components/sectionLink/sectionLink';
import { Link, useMatch, useSearchParams } from 'react-router-dom';
import { selectCategories } from 'store/page/pageSlice';
import { logout, selectUser } from 'store/auth/authSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import map from 'lodash-es/map';
import Avatar from 'components/avatar/avatar';
import Skeleton from '@mui/material/Skeleton';
import { useNavigate } from 'react-router-dom';
import ExitIcon from 'icons/exit';
import ContextActionsComponent from 'controls/contextActions/contextActions';
import filter from 'lodash-es/filter';
import includes from 'lodash-es/includes';
import { getCommonOmitActions } from 'utils/hooked';
import { useCurrentCategory } from 'hooks/useCategoryId';
import cx from 'classnames';

export default function DesktopHeader({
  pageIsLoading,
}: {
  pageIsLoading: boolean;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const categories = useAppSelector(selectCategories);
  const name = user?.name;
  const surname = user?.surname;
  const actionsIconRef = useRef<HTMLDivElement>(null);
  const omitedActions = getCommonOmitActions();
  const [searchParams] = useSearchParams();
  const mainChecker = useMatch('/:name/*')?.params.name;
  const isSearchContext = mainChecker === 'search' || mainChecker === 'mapping';
  const currentCategory = useCurrentCategory();

  return (
    <div className={cx(style.container, 'adaptive-header__container')}>
      <div className={style.left}>
        <Link className={'adaptive-header__logo'} to="/">
          <Logo className={style.logo}></Logo>
        </Link>
        <div className={style.sections}>
          {pageIsLoading ? (
            <>
              <Skeleton variant="text" width={150} height={22} />
              <Skeleton variant="text" width={150} height={22} />
              <Skeleton variant="text" width={150} height={22} />
            </>
          ) : (
            map(categories, (category) => {
              if (isSearchContext) {
                searchParams.set('category', category.id);
              }

              searchParams.delete('page');

              return (
                <SectionLink
                  className={'adaptive-f18'}
                  key={category.id}
                  categoryId={category.id}
                  to={
                    isSearchContext
                      ? `/search?${searchParams.toString()}`
                      : `${category.href}?${searchParams.toString()}`
                  }
                >
                  {category.id}
                </SectionLink>
              );
            })
          )}
        </div>
      </div>
      <div className={cx(style.right, 'noselect')}>
        <Avatar
          className={cx(style['avatar-offset'], 'adaptive-header__avatar')}
          onClick={() =>
            navigate(`/cabinet?category=Фотографии`, {
              replace: false,
            })
          }
        />
        {!user ? (
          <Skeleton variant="text" width={150} height={22} />
        ) : (
          <div
            className={style.userName}
            onClick={() => {
              if (actionsIconRef.current) {
                actionsIconRef.current.click();
              }
            }}
          >
            <span
              className={'adaptive-header__name'}
            >{`${name} ${surname?.[0]}.`}</span>
            <ContextActionsComponent
              ref={actionsIconRef}
              actions={filter(
                [
                  {
                    type: 'toProfile',
                    handler: () => {
                      navigate(`/cabinet?category=Фотографии`, {
                        replace: false,
                      });
                    },
                  },
                  {
                    type: 'toTags',
                    handler: () => {
                      navigate(`/tags`, {
                        replace: false,
                      });
                    },
                  },
                  {
                    type: 'toAdmin',
                    handler: () => {
                      navigate(`/admin/requests`, {
                        replace: false,
                      });
                    },
                  },
                  {
                    type: 'toUpload',
                    handler: () => {
                      navigate(`/upload#${currentCategory.id}`, {
                        replace: false,
                      });
                    },
                  },
                ],
                (action) => !includes(omitedActions, action.type)
              )}
              iconType={'navigation'}
              className={style.expand}
              bubbleClassName={style.bubble}
            ></ContextActionsComponent>
          </div>
        )}

        <ExitIcon
          className={style.exit}
          onClick={() => dispatch(logout())}
        ></ExitIcon>
      </div>
    </div>
  );
}
